import { createContext, useContext, useMemo } from "react";
import { Outlet, useLocation, useOutletContext } from "react-router-dom";
import Joyride from "react-joyride";
import { useTheme } from "@mui/material/styles";

import {
  useTutorial,
  TutorialCompletionModal,
  TutorialJoyrideTooltip,
  getTutorialJoyrideStyles,
  TUTORIAL_FLOATER_PROPS,
  useTutorialsEnabled,
} from "../../Tutorials/index.js";
import { CONTENT_GENERATOR_TUTORIAL_COMPLETION_DESCRIPTION } from "../../Tutorials/contentGenerator/useTutorial.js";

const ContentTutorialContext = createContext(null);

const noop = () => {};
// Pages mounted outside the content layout (e.g. the client portal) have no tutorial.
const NO_TUTORIAL = Object.freeze({
  isTutorialActive: false,
  tutorialMock: null,
  startTutorial: noop,
  stopTutorial: noop,
});

/** Tutorial state shared by every content page (list, batch, piece, group). */
export function useContentTutorial() {
  return useContext(ContentTutorialContext) || NO_TUTORIAL;
}

/**
 * Layout route for /HerramientasIA/contenidos/*: keeps the guided tutorial alive while it moves
 * between the list, the batch page and a piece page, and renders its tooltips.
 */
export function ContentTutorialLayout() {
  const outletContext = useOutletContext();
  const { pathname } = useLocation();
  const theme = useTheme();
  const tutorialsEnabled = useTutorialsEnabled();
  const tutorial = useTutorial();
  const {
    isTutorialActive,
    tutorialMock,
    tutorialScene,
    steps,
    stepIndex,
    joyrideRun,
    joyrideKey,
    startTutorial,
    stopTutorial,
    handleJoyrideCallback,
    showTutorialSuccess,
    setShowTutorialSuccess,
  } = tutorial;

  const contextValue = useMemo(
    () => ({
      isTutorialActive: tutorialsEnabled && isTutorialActive,
      tutorialMock: tutorialsEnabled ? tutorialMock : null,
      startTutorial: tutorialsEnabled ? startTutorial : noop,
      stopTutorial,
    }),
    [isTutorialActive, startTutorial, stopTutorial, tutorialMock, tutorialsEnabled]
  );

  const zIndex = tutorialScene?.zIndex || 10000;
  const styles = useMemo(() => getTutorialJoyrideStyles(theme, zIndex), [theme, zIndex]);
  const allowScroll = Boolean(tutorialScene?.scroll);

  return (
    <ContentTutorialContext.Provider value={contextValue}>
      <Outlet key={pathname} context={outletContext} />

      {tutorialsEnabled && isTutorialActive && (
        <Joyride
          floaterProps={TUTORIAL_FLOATER_PROPS}
          key={`content-tutorial-${joyrideKey}`}
          steps={steps}
          stepIndex={stepIndex}
          run={tutorialsEnabled && joyrideRun}
          continuous
          showSkipButton={false}
          showProgress={false}
          scrollToFirstStep={allowScroll}
          disableOverlayClose
          disableCloseOnEsc
          disableScrolling={!allowScroll}
          // Without it Joyride sets overflow: initial on a scroll parent that does not overflow
          // (the layout's main container), which then stops scrolling for the rest of the session.
          disableScrollParentFix
          locale={{ close: "Cerrar", last: "Siguiente", next: "Siguiente" }}
          tooltipComponent={TutorialJoyrideTooltip}
          styles={styles}
          callback={handleJoyrideCallback}
        />
      )}

      <TutorialCompletionModal
        open={tutorialsEnabled && showTutorialSuccess}
        onClose={() => setShowTutorialSuccess(false)}
        description={CONTENT_GENERATOR_TUTORIAL_COMPLETION_DESCRIPTION}
      />
    </ContentTutorialContext.Provider>
  );
}
